/**
 * A best-effort human label for the browser enrolling a passkey — CU-86ajzr8x1
 * (adversarial round 1, F2).
 *
 * ⚠️ THIS IS A CONVENIENCE, NOT AN IDENTIFIER, and the wire type says so
 * explicitly: `deviceLabel` is client-supplied, best-effort, and MUST NEVER be
 * treated as identifying or used as a revoke key. Two devices can produce the
 * same label and nothing prevents it — `createdAt` is the reliable discriminator
 * and `credId` is the revoke target. Its only job is to make a management list
 * scannable: "Chrome on macOS" beats "Unnamed passkey" when a patient is
 * deciding which row is the phone they lost.
 *
 * ⚠️ WHY THIS EXISTS AT ALL. Before F2 the portal never sent one, so EVERY row
 * rendered the `'Unnamed passkey'` fallback and every confirmation read "Remove
 * this unnamed passkey, enrolled …" — the list's disambiguation story degraded to
 * date-only. The field was declared at three layers (`passkeyClient`,
 * `passkeyFlows`, the hook's deps type) and consumed by the UI, but no caller
 * could set it.
 *
 * ⚠️ NOT A SECURITY CONTROL AND NOT PHI. It is coarse by design: a browser
 * family and an OS family, never a version, never a model, never a serial. A
 * narrower label would be a fingerprinting surface stored server-side against a
 * patient's account, which is a worse trade than a slightly vaguer list.
 *
 * ⚠️ ORDER MATTERS IN BOTH LADDERS BELOW — the checks are deliberately not
 * alphabetical. Every Chromium browser's UA also contains "Chrome"/"Safari", and
 * every iOS browser's UA contains "Safari", so the specific brands must be
 * tested BEFORE the generic ones. Reordering these silently mislabels: Edge
 * becomes Chrome, and Chrome on iOS becomes Safari.
 */

/** Browser family, most specific first. */
function browserName(ua: string): string | null {
  // Edge and Opera are Chromium-based and carry "Chrome" in their UA.
  if (/\bEdg(?:e|A|iOS)?\//.test(ua)) return 'Edge';
  if (/\bOPR\/|\bOpera\//.test(ua)) return 'Opera';
  // Samsung Internet also carries "Chrome"; the portal already special-cases it
  // elsewhere (see SamsungBrowserBanner).
  if (/SamsungBrowser\//.test(ua)) return 'Samsung Internet';
  // Chrome on iOS is "CriOS"; Firefox on iOS is "FxiOS". Both also say "Safari".
  if (/\bCriOS\//.test(ua)) return 'Chrome';
  if (/\bFxiOS\//.test(ua)) return 'Firefox';
  if (/\bFirefox\//.test(ua)) return 'Firefox';
  if (/\bChrome\//.test(ua)) return 'Chrome';
  // Safari last: every browser above claims it too.
  if (/\bSafari\//.test(ua)) return 'Safari';
  return null;
}

/** OS family, most specific first. */
function osName(ua: string): string | null {
  if (/\biPhone\b/.test(ua)) return 'iPhone';
  if (/\biPad\b/.test(ua)) return 'iPad';
  // ⚠️ A modern iPad reports as "Macintosh" and is separated only by touch
  // points — the same test `useDeviceDetection` uses. Checked before macOS.
  if (/\bMacintosh\b/.test(ua)) {
    const touchCapable = typeof navigator !== 'undefined' && (navigator.maxTouchPoints ?? 0) > 1;
    return touchCapable ? 'iPad' : 'macOS';
  }
  if (/\bAndroid\b/.test(ua)) return 'Android';
  if (/\bWindows\b/.test(ua)) return 'Windows';
  // CrOS before Linux: a Chromebook's UA contains both.
  if (/\bCrOS\b/.test(ua)) return 'ChromeOS';
  if (/\bLinux\b/.test(ua)) return 'Linux';
  return null;
}

/**
 * Build the label, e.g. `"Chrome on macOS"`, `"Safari on iPhone"`.
 *
 * Returns `undefined` when neither half can be determined, so the caller omits
 * the field entirely rather than sending a useless placeholder — the server
 * stores what it is given, and `"Unknown browser on Unknown OS"` would be worse
 * than the UI's own `'Unnamed passkey'` fallback.
 */
export function describeThisBrowser(userAgent?: string): string | undefined {
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
  if (!ua) return undefined;

  const browser = browserName(ua);
  const os = osName(ua);

  if (browser && os) return `${browser} on ${os}`;
  // A half-label still disambiguates two rows, so it is worth sending.
  return browser ?? os ?? undefined;
}
